(() => {
  "use strict";

  window.zag = window.zag || {};

  window.zag.components.ZagSearch = class extends (
    window.zag.components.ZagComponent
  ) {
    connectedCallback() {
      this.events.add(
        this.searchInput,
        "input",
        window.zag.utils.debounce(this.onSearchInput, 800)
      );

      if (!this.isSearchTemplate) {
        this.events.add(this.searchButton, "click", this.onSearchButtonClick);
        this.searchTabs.forEach((tab) => {
          this.events.add(tab, "click", this.onSearchTabClick);
        });
      } else {
        if (this.clearSearchQueryButton) {
          this.events.add(
            this.clearSearchQueryButton,
            "click",
            this.onClearSearchQueryClick
          );
        }
      }
    }

    get isSearchTemplate() {
      return this.cache.get("dom", "isSearchTemplate", () => {
        return window.zag.utils.documentModifiers.has("template:search");
      });
    }

    get searchInput() {
      return this.cache.get("dom", "searchInput", () => {
        return this.querySelector(".search-input");
      });
    }

    get searchButton() {
      return this.cache.get("dom", "searchButton", () => {
        return this.querySelector(".confirm-search-button");
      });
    }

    get clearSearchQueryButton() {
      return this.cache.get("dom", "clearSearchQueryButton", () => {
        return this.querySelector(".clear-search-query");
      });
    }

    get searchTabs() {
      return Array.from(this.querySelectorAll(".search-tab"));
    }

    get resourceResultsContainers() {
      return Array.from(this.querySelectorAll(".resource-results-container"));
    }

    get searchBody() {
      return this.cache.get("dom", "searchBody", () => {
        return this.querySelector(".search-body");
      });
    }

    get searchGrid() {
      return this.cache.get("dom", "searchGrid", () => {
        return this.querySelector(".result-grid");
      });
    }

    get sectionId() {
      return this.cache.get("dom", "sectionId", () => {
        return this.isSearchTemplate
          ? window.zag.dom.search.section.id.split("shopify-section-").at(-1)
          : window.zag.dom.header.section.id.split("shopify-section-").at(-1);
      });
    }

    onSearchInput = async () => {
      const query = this.searchInput.value.trim();

      if (this.isSearchTemplate) {
        if (query.length === 0) {
          const url = new URL(window.location);
          url.searchParams.delete("q");
          window.history.replaceState({}, "", url.toString());
          window.location.reload();
          return;
        }
        await this.updateSearchResults(query);
      } else {
        if (query.length === 0) {
          return;
        }
        await this.updatePredictiveSearch(query);
      }
    };

    updatePredictiveSearch = async (query) => {
      const queryParameters = `q=${query}&resources[type]=product,collection,page,article&resources[limit_scope]=each&resources[options][unavailable_products]=last&resources[options][fields]=title,product_type,variants.title,vendor,body&section_id=${this.sectionId}`;

      try {
        const sectionMarkup = await fetch(`/search/suggest?${queryParameters}`)
          .then((response) => response.text())
          .then((text) => {
            return new DOMParser()
              .parseFromString(text, "text/html")
              .querySelector("zag-search");
          });

        this.searchBody.innerHTML =
          sectionMarkup.querySelector(".search-body").innerHTML;
        this.searchBody.setAttribute(
          "data-resource-type-count",
          sectionMarkup
            .querySelector(".search-body")
            .getAttribute("data-resource-type-count")
        );
      } catch (error) {
        console.error("Error updating predictive search:", error);
      }
    };

    updateSearchResults = async (query) => {
      const url = new URL(window.location);
      url.searchParams.set("q", query);

      url.searchParams.delete("page");

      try {
        const response = await fetch(url.toString(), {
          headers: {
            "X-Requested-With": "XMLHttpRequest",
          },
        });

        const text = await response.text();
        const newDocument = new DOMParser().parseFromString(text, "text/html");
        const newSearchGrid = newDocument.querySelector(".result-grid");

        if (newSearchGrid && this.searchGrid) {
          this.searchGrid.innerHTML = newSearchGrid.innerHTML;
        }

        window.history.replaceState({}, "", url.toString());
      } catch (error) {
        console.error("Error updating search results:", error);
      }
    };

    onSearchButtonClick = () => {
      const query = this.searchInput.value.trim();
      if (query.length === 0) {
        window.location.href = "/search";
      }

      window.location.href = `/search?q=${encodeURIComponent(query)}`;
    };

    onSearchTabClick = (event) => {};

    onClearSearchQueryClick = (event) => {
      event.preventDefault();

      this.searchInput.value = "";
    };
  };
  if (!window.customElements.get("zag-search")) {
    window.customElements.define("zag-search", window.zag.components.ZagSearch);
  }

  window.zag.components.SearchTab = class extends (
    window.zag.components.ZagComponent
  ) {
    connectedCallback() {
      this.events.add(this, "click", this.onClick);
    }

    get zagSearch() {
      return this.cache.get("dom", "zagSearch", () => {
        return this.closest("zag-search");
      });
    }

    onClick = (event) => {
      const tab = event.currentTarget;

      if (tab.getAttribute("aria-selected") === "true") {
        return;
      }

      this.zagSearch.searchTabs.forEach((searchTab) => {
        searchTab.setAttribute(
          "aria-selected",
          searchTab === tab ? "true" : "false"
        );
      });

      const type = tab.dataset.type;

      this.zagSearch.resourceResultsContainers.forEach((container) => {
        container.dataset.type === type
          ? container.setAttribute("aria-selected", "true")
          : container.setAttribute("aria-selected", "false");
      });
    };
  };
  if (!window.customElements.get("search-tab")) {
    window.customElements.define("search-tab", window.zag.components.SearchTab);
  }

  window.zag.components.SearchGrid = class extends (
    window.zag.components.ZagComponent
  ) {
    connectedCallback() {
      this.events.add(
        this.showSortOptionsButton,
        "click",
        this.onShowSortOptionsClick
      );
      this.events.add(document, "click", this.onDocumentClick);

      this.sortOptions.forEach((option) => {
        this.events.add(option, "click", this.onSortOptionClick);
      });

      if (this.isSearchTemplate) {
        this.events.add(this.showFiltersButton, "click", this.openFiltersPopin);
        this.events.add(this.closePopinButton, "click", this.closeFiltersPopin);
        this.events.add(this.filtersPopinContainer, "click", (event) => {
          if (event.target === this.filtersPopinContainer) {
            this.closeFiltersPopin();
          }
        });

        this.events.add(
          this.confirmFilterSelectionButton,
          "click",
          this.onConfirmFilterSelectionClick
        );

        this.appliedFiltersCards.forEach((card) => {
          this.events.add(card, "click", this.onAppliedFilterCardClick);
        });

        this.events.add(
          this.clearFiltersButton,
          "click",
          this.onClearFiltersClick
        );

        this.filterValueCards.forEach((card) => {
          this.events.add(card, "click", this.onFilterValueCardClick);
        });

        this.events.add(
          this,
          "dual-handle-range:update",
          this.onPriceFilterUpdate
        );

        this.updateAppliedFilterCount();
      }
    }

    get zagSearch() {
      return this.cache.get("dom", "zagSearch", () => {
        return this.closest("zag-search");
      });
    }

    get isSearchTemplate() {
      return this.cache.get("dom", "isSearchTemplate", () => {
        return window.zag.utils.documentModifiers.has("template:search");
      });
    }

    get showFiltersButton() {
      return this.cache.get("dom", "showFiltersButton", () => {
        return this.querySelector(".show-filters");
      });
    }

    get showSortOptionsButton() {
      return this.cache.get("dom", "showSortOptionsButton", () => {
        return this.querySelector(".show-sort-options");
      });
    }

    get sortOptionsContainer() {
      return this.cache.get("dom", "sortOptionsContainer", () => {
        return this.querySelector(".sort-options-container");
      });
    }

    get sortOptions() {
      return this.cache.get("dom", "sortOptions", () => {
        return Array.from(this.querySelectorAll(".sort-option"));
      });
    }

    get filtersPopinContainer() {
      return this.cache.get("dom", "filtersPopinContainer", () => {
        return this.querySelector(".filters-popin-container");
      });
    }

    get filtersPopin() {
      return this.cache.get("dom", "filtersPopin", () => {
        return this.querySelector(".filters-popin");
      });
    }

    get closePopinButton() {
      return this.cache.get("dom", "closePopinButton", () =>
        this.querySelector(".popin-close")
      );
    }

    get appliedFiltersCards() {
      return this.cache.get("dom", "appliedFiltersCards", () => {
        return Array.from(this.querySelectorAll(".applied-filter-card"));
      });
    }

    get appliedFiltersTitle() {
      return this.cache.get("dom", "appliedFiltersTitle", () => {
        return this.querySelector(".applied-filters-title");
      });
    }

    get appliedFilters() {
      return this.cache.get("dom", "appliedFilters", () => {
        return this.querySelector(".applied-filters");
      });
    }

    get appliedFilterCount() {
      return this.appliedFiltersCards.reduce((count, card) => {
        return count + (card.getAttribute("aria-selected") === "true" ? 1 : 0);
      }, 0);
    }

    get clearFiltersButton() {
      return this.cache.get("dom", "clearFiltersButton", () => {
        return this.querySelector(".clear-filters");
      });
    }

    get filterValueCards() {
      return this.cache.get("dom", "filterValueCards", () => {
        return Array.from(this.querySelectorAll(".filter-value"));
      });
    }

    get priceFilterSlider() {
      return this.cache.get("dom", "priceFilterSlider", () => {
        return this.querySelector("dual-handle-range");
      });
    }

    get confirmFilterSelectionButton() {
      return this.cache.get("dom", "confirmSelectionButton", () => {
        return this.querySelector(".confirm-filter-selection");
      });
    }

    get endpoint() {
      const url = new URL(window.location);
      url.searchParams.set("page", Number(this.pageNumber) + 1);
      return url.toString();
    }

    onShowSortOptionsClick = (event) => {
      event.stopPropagation();

      if (this.showSortOptionsButton.hasAttribute("data-active")) {
        this.showSortOptionsButton.removeAttribute("data-active");
      } else {
        this.showSortOptionsButton.setAttribute("data-active", "");
      }
    };

    onSortOptionClick = (event) => {
      const sortOption = event.currentTarget;

      const url = new URL(window.location);
      url.searchParams.set("sort_by", sortOption.dataset.sortOption);
      window.location.href = url;
    };

    onDocumentClick = (event) => {
      if (!this.showSortOptionsButton.hasAttribute("data-active")) return;

      if (!this.sortOptionsContainer.contains(event.target)) {
        this.showSortOptionsButton.removeAttribute("data-active");
      }
    };

    openFiltersPopin = (event) => {
      if (event) event.preventDefault();
      window.zag.utils.documentScroll.isLocked = true;
      this.modifiers.add("filters-popin");
    };

    closeFiltersPopin = (event) => {
      if (event) event.preventDefault();
      window.zag.utils.documentScroll.isLocked = false;
      this.modifiers.remove("filters-popin");
    };

    onConfirmFilterSelectionClick = (event) => {
      event.preventDefault();

      const url = new URL(window.location);

      const currentQuery = url.searchParams.get("q");

      const paramsToDelete = [];
      for (const [key] of url.searchParams) {
        if (key.startsWith("filter") || key === "page") {
          paramsToDelete.push(key);
        }
      }
      paramsToDelete.forEach((param) => url.searchParams.delete(param));

      if (currentQuery) {
        url.searchParams.set("q", currentQuery);
      }

      this.appliedFiltersCards.forEach((card) => {
        if (card.getAttribute("aria-selected") === "true") {
          if (card.dataset.url) {
            const filterParam = decodeURIComponent(card.dataset.url);
            const [key, value] = filterParam.split("=");
            url.searchParams.append(key, value);
          } else if (card.dataset.minUrl && card.dataset.maxUrl) {
            const minValue = Number(
              this.priceFilterSlider.minValue.dataset.value
            );
            const maxValue = Number(
              this.priceFilterSlider.maxValue.dataset.value
            );
            const minLimit = Number(this.priceFilterSlider.dataset.min);
            const maxLimit = Number(this.priceFilterSlider.dataset.max);

            if (minValue > minLimit) {
              const minUrl = card.dataset.minUrl.replace(
                "[[min_price]]",
                minValue.toString()
              );
              const [minKey, minVal] = decodeURIComponent(minUrl).split("=");
              url.searchParams.append(minKey, minVal);
            }

            if (maxValue < maxLimit) {
              const maxUrl = card.dataset.maxUrl.replace(
                "[[max_price]]",
                maxValue.toString()
              );
              const [maxKey, maxVal] = decodeURIComponent(maxUrl).split("=");
              url.searchParams.append(maxKey, maxVal);
            }
          }
        }
      });

      this.closeFiltersPopin();

      window.location.href = url.toString();
    };

    onAppliedFilterCardClick = (event) => {
      const card = event.currentTarget;
      if (!event.target.closest(".remove-filter-value")) return;

      event.preventDefault();
      event.stopPropagation();

      if (card.dataset.url) {
        const filterUrl = card.dataset.url;
        const correspondingFilterValue = this.filterValueCards.find(
          (filterCard) => filterCard.dataset.url === filterUrl
        );

        if (correspondingFilterValue) {
          correspondingFilterValue.setAttribute("aria-selected", "false");
        }
        card.setAttribute("aria-selected", "false");
      } else if (card.dataset.minUrl) {
        if (this.priceFilterSlider) {
          const minLimit = Number(this.priceFilterSlider.dataset.min);
          const maxLimit = Number(this.priceFilterSlider.dataset.max);
          this.priceFilterSlider.setValues(minLimit, maxLimit);

          const currencySymbol = card.dataset.currencySymbol || "";
          const paragraph = card.querySelector("p");
          if (paragraph) {
            paragraph.textContent = `${minLimit}-${maxLimit} ${currencySymbol}`;
          }
        }
        card.setAttribute("aria-selected", "false");
      }

      this.updateAppliedFilterCount();
    };

    onClearFiltersClick = (event) => {
      event.preventDefault();

      this.filterValueCards.forEach((card) => {
        card.setAttribute("aria-selected", "false");
      });

      this.appliedFiltersCards.forEach((card) => {
        card.setAttribute("aria-selected", "false");
      });

      if (this.priceFilterSlider) {
        const minLimit = Number(this.priceFilterSlider.dataset.min);
        const maxLimit = Number(this.priceFilterSlider.dataset.max);
        this.priceFilterSlider.setValues(minLimit, maxLimit);

        const priceAppliedCard = this.appliedFiltersCards.find(
          (card) => card.dataset.minUrl
        );
        if (priceAppliedCard) {
          const currencySymbol = priceAppliedCard.dataset.currencySymbol || "";
          const paragraph = priceAppliedCard.querySelector("p");
          if (paragraph) {
            paragraph.textContent = `${minLimit}-${maxLimit} ${currencySymbol}`;
          }
        }
      }

      this.updateAppliedFilterCount();
    };

    onFilterValueCardClick = (event) => {
      const card = event.currentTarget;
      event.preventDefault();

      const isCurrentlySelected = card.getAttribute("aria-selected") === "true";
      const newState = !isCurrentlySelected;

      card.setAttribute("aria-selected", newState.toString());

      const filterUrl = card.dataset.url;
      const correspondingAppliedCard = this.appliedFiltersCards.find(
        (appliedCard) => appliedCard.dataset.url === filterUrl
      );

      if (correspondingAppliedCard) {
        correspondingAppliedCard.setAttribute(
          "aria-selected",
          newState.toString()
        );
      }

      this.updateAppliedFilterCount();
    };

    onPriceFilterUpdate = (event) => {
      const { minValue, maxValue, minLimit, maxLimit } = event.detail;

      const priceAppliedCard = this.appliedFiltersCards.find(
        (card) => card.dataset.minUrl
      );

      if (priceAppliedCard) {
        const isMinSet = minValue > minLimit;
        const isMaxSet = maxValue < maxLimit;
        const isPriceFilterActive = isMinSet || isMaxSet;

        priceAppliedCard.setAttribute(
          "aria-selected",
          isPriceFilterActive.toString()
        );

        const currencySymbol = priceAppliedCard.dataset.currencySymbol || "";
        const paragraph = priceAppliedCard.querySelector("p");
        if (paragraph) {
          paragraph.textContent = `${minValue}-${maxValue} ${currencySymbol}`;
        }

        this.updateAppliedFilterCount();
      }
    };

    updateAppliedFilterCount = () => {
      const count = this.appliedFilterCount;

      if (this.appliedFiltersTitle) {
        const span = this.appliedFiltersTitle.querySelector("span");
        if (span) {
          span.textContent = count;
        }
      }

      if (this.appliedFilters) {
        this.appliedFilters.setAttribute("data-applied-filter-count", count);
      }
    };
  };
  if (!window.customElements.get("search-grid")) {
    window.customElements.define(
      "search-grid",
      window.zag.components.SearchGrid
    );
  }

  window.zag.components.DualHandleRange = class extends (
    window.zag.components.ZagComponent
  ) {
    constructor() {
      super();

      this.activeHandle = null;
      this.dragState = {
        isDragging: false,
        top: 0,
        left: 0,
        x: 0,
        y: 0,
        startTime: 0,
      };
    }

    connectedCallback() {
      this.updateUI();

      this.events.add(this.minHandle, "pointerdown", (event) =>
        this.onPointerDown(event, this.minHandle)
      );
      this.events.add(this.maxHandle, "pointerdown", (event) =>
        this.onPointerDown(event, this.maxHandle)
      );
    }

    get minValue() {
      return this.cache.get("state", "minValue", () => {
        return this.querySelector(".range-value--min");
      });
    }

    get maxValue() {
      return this.cache.get("state", "maxValue", () => {
        return this.querySelector(".range-value--max");
      });
    }

    get minHandle() {
      return this.cache.get("dom", "minHandle", () => {
        return this.querySelector(".range-handle--min");
      });
    }

    get maxHandle() {
      return this.cache.get("dom", "maxHandle", () => {
        return this.querySelector(".range-handle--max");
      });
    }

    get rangeFill() {
      return this.cache.get("dom", "rangeFill", () => {
        return this.querySelector(".range-fill");
      });
    }

    onPointerDown = (event, handle) => {
      event.preventDefault();

      this.activeHandle = handle;
      this.dragState = {
        isDragging: true,
        x: event.clientX,
        y: event.clientY,
      };

      this.events.add(document, "pointermove", this.onPointerMove, {
        passive: false,
      });
      this.events.add(document, "pointerup", this.onPointerUp);
    };

    onPointerMove = (event) => {
      if (!this.dragState.isDragging) return;
      event.preventDefault();

      const rect = this.getBoundingClientRect();
      const relativeX = event.clientX - rect.left;
      const percentage = Math.max(
        0,
        Math.min(100, (relativeX / rect.width) * 100)
      );

      const minRangeValue = Number(this.dataset.min);
      const maxRangeValue = Number(this.dataset.max);

      const minHandleValue = Number(this.minValue.dataset.value);
      const maxHandleValue = Number(this.maxValue.dataset.value);

      const value = Math.round(
        (percentage / 100) * (maxRangeValue - minRangeValue) + minRangeValue
      );

      if (this.activeHandle === this.minHandle) {
        this.minValue.dataset.value = Math.min(value, maxHandleValue);
      } else {
        this.maxValue.dataset.value = Math.max(value, minHandleValue);
      }

      this.updateUI();
    };

    onPointerUp = (event) => {
      if (!this.dragState.isDragging) return;
      this.dragState.isDragging = false;

      this.events.remove(document, "pointermove", this.onPointerMove);
      this.events.remove(document, "pointerup", this.onPointerUp);

      this.dispatchEvent(
        new CustomEvent("dual-handle-range:update", {
          bubbles: true,
          detail: {
            minValue: Number(this.minValue.dataset.value),
            maxValue: Number(this.maxValue.dataset.value),
            minLimit: Number(this.dataset.min),
            maxLimit: Number(this.dataset.max),
          },
        })
      );
    };

    updateUI = () => {
      const minPercentage =
        ((this.minValue.dataset.value - this.dataset.min) /
          (this.dataset.max - this.dataset.min)) *
        100;
      const maxPercentage =
        ((this.maxValue.dataset.value - this.dataset.min) /
          (this.dataset.max - this.dataset.min)) *
        100;

      this.style.setProperty("--min-handle-offset", `${minPercentage}%`);
      this.style.setProperty("--max-handle-offset", `${maxPercentage}%`);
      this.style.setProperty("--range-fill-offset", `${minPercentage}%`);
      this.style.setProperty(
        "--range-fill-size",
        `${maxPercentage - minPercentage}%`
      );
    };

    setValues = (minValue, maxValue) => {
      const minLimit = Number(this.dataset.min);
      const maxLimit = Number(this.dataset.max);

      this.minValue.dataset.value = Math.max(
        minLimit,
        Math.min(minValue, maxValue)
      );
      this.maxValue.dataset.value = Math.min(
        maxLimit,
        Math.max(minValue, maxValue)
      );

      this.updateUI();
    };
  };
  if (!window.customElements.get("dual-handle-range")) {
    window.customElements.define(
      "dual-handle-range",
      window.zag.components.DualHandleRange
    );
  }
})();
